import React, { useState } from "react";

type Piece = string | null;
type Board = Piece[][];
type Pos = [number, number];

const initialBoard: Board = [
    ["r","n","b","q","k","b","n","r"],
    ["p","p","p","p","p","p","p","p"],
    [null,null,null,null,null,null,null,null],
    [null,null,null,null,null,null,null,null],
    [null,null,null,null,null,null,null,null],
    [null,null,null,null,null,null,null,null],
    ["P","P","P","P","P","P","P","P"],
    ["R","N","B","Q","K","B","N","R"],
];

const pieceMap: Record<string, string> = {
    p: "♟", r: "♜", n: "♞", b: "♝", q: "♛", k: "♚",
    P: "♙", R: "♖", N: "♘", B: "♗", Q: "♕", K: "♔",
};

const diceLabel: Record<string, string> = {
    p: "PAWN",
    n: "KNIGHT",
    b: "BISHOP",
    r: "ROOK",
    q: "QUEEN",
    k: "KING",
};

const inBounds = (r: number, c: number) =>
    r >= 0 && r < 8 && c >= 0 && c < 8;

const ChessPage: React.FC = () => {
    const [board, setBoard] = useState<Board>(initialBoard);
    const [selected, setSelected] = useState<Pos | null>(null);
    const [legalMoves, setLegalMoves] = useState<Pos[]>([]);
    const [dice, setDice] = useState<string | null>(null);
    const [turn, setTurn] = useState<"red" | "blue">("red");
    const [winner, setWinner] = useState<"red" | "blue" | null>(null);

    const isRed = turn === "red";

    const turnGlow = isRed
        ? "shadow-[0_0_30px_rgba(239,68,68,0.5)] border-red-600"
        : "shadow-[0_0_30px_rgba(59,130,246,0.5)] border-blue-600";

    const turnText = isRed ? "text-red-400" : "text-blue-400";

    const isOwn = (p: string) =>
        turn === "red" ? p === p.toLowerCase() : p === p.toUpperCase();

    const isEnemy = (p: string) => !isOwn(p);

    const pieceColor = (p: string) =>
        p === p.toUpperCase() ? "text-blue-400" : "text-red-400";

    const getMoves = (r: number, c: number, piece: string): Pos[] => {
        const moves: Pos[] = [];
        const isBlue = piece === piece.toUpperCase();
        const dir = isBlue ? -1 : 1;
        const startRow = isBlue ? 6 : 1;

        const add = (nr: number, nc: number) => {
            if (!inBounds(nr, nc)) return;
            if (!board[nr][nc]) moves.push([nr, nc]);
            else if (isEnemy(board[nr][nc]!)) moves.push([nr, nc]);
        };

        switch (piece.toLowerCase()) {
            case "p":
                if (!board[r + dir]?.[c]) {
                    moves.push([r + dir, c]);
                    if (r === startRow && !board[r + dir * 2]?.[c])
                        moves.push([r + dir * 2, c]);
                }
                [-1, 1].forEach(dc => {
                    const t = board[r + dir]?.[c + dc];
                    if (t && isEnemy(t)) moves.push([r + dir, c + dc]);
                });
                break;

            case "n":
                [[1,2],[2,1],[-1,2],[-2,1],[1,-2],[2,-1],[-1,-2],[-2,-1]]
                    .forEach(([dr, dc]) => add(r + dr, c + dc));
                break;

            case "b":
            case "r":
            case "q": {
                const dirs: number[][] = [];
                if (piece.toLowerCase() !== "r")
                    dirs.push([1,1],[1,-1],[-1,1],[-1,-1]);
                if (piece.toLowerCase() !== "b")
                    dirs.push([1,0],[-1,0],[0,1],[0,-1]);

                dirs.forEach(([dr, dc]) => {
                    let nr = r + dr, nc = c + dc;
                    while (inBounds(nr, nc)) {
                        if (!board[nr][nc]) moves.push([nr, nc]);
                        else {
                            if (isEnemy(board[nr][nc]!)) moves.push([nr, nc]);
                            break;
                        }
                        nr += dr; nc += dc;
                    }
                });
                break;
            }

            case "k":
                [-1,0,1].forEach(dr =>
                    [-1,0,1].forEach(dc => {
                        if (dr || dc) add(r + dr, c + dc);
                    })
                );
                break;
        }

        return moves;
    };

    const getMovablePieces = (): string[] => {
        const set = new Set<string>();
        board.forEach((row, r) =>
            row.forEach((p, c) => {
                if (!p || !isOwn(p)) return;
                if (getMoves(r, c, p).length)
                    set.add(p.toLowerCase());
            })
        );
        return Array.from(set);
    };

    const rollDice = () => {
        if (dice || winner) return;
        const movable = getMovablePieces();
        if (!movable.length) return;
        setDice(movable[Math.floor(Math.random() * movable.length)]);
        setSelected(null);
        setLegalMoves([]);
    };

    const handleClick = (r: number, c: number) => {
        if (winner) return;
        const piece = board[r][c];

        if (piece && dice && isOwn(piece) && piece.toLowerCase() === dice) {
            setSelected([r, c]);
            setLegalMoves(getMoves(r, c, piece));
            return;
        }

        if (selected) {
            const isLegal = legalMoves.some(m => m[0] === r && m[1] === c);
            if (!isLegal) return;

            const newBoard = board.map(row => [...row]);
            const [sr, sc] = selected;
            const captured = newBoard[r][c];

            newBoard[r][c] = newBoard[sr][sc];
            newBoard[sr][sc] = null;

            if (captured?.toLowerCase() === "k") {
                const win = turn;
                setWinner(win);

                const cleared = newBoard.map(row =>
                    row.map(p => {
                        if (!p) return null;
                        return win === "red"
                            ? p === p.toLowerCase() ? p : null
                            : p === p.toUpperCase() ? p : null;
                    })
                );
                setBoard(cleared);
                return;
            }

            setBoard(newBoard);
            setSelected(null);
            setLegalMoves([]);
            setDice(null);
            setTurn(turn === "red" ? "blue" : "red");
        }
    };

    const resetGame = () => {
        setBoard(initialBoard);
        setSelected(null);
        setLegalMoves([]);
        setDice(null);
        setTurn("red");
        setWinner(null);
    };

    return (
        <div className="starry-bg min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-white flex items-center justify-center p-4 font-mono">
            <div className={`flex flex-col md:flex-row gap-6 p-4 bg-gradient-to-br border transition-all duration-500 ${turnGlow}`}>

                <div className="grid grid-cols-8 border border-zinc-700">
                    {board.map((row, r) =>
                        row.map((piece, c) => {
                            const isDark = (r + c) % 2;
                            const isMove = legalMoves.some(m => m[0] === r && m[1] === c);

                            return (
                                <div
                                    key={`${r}-${c}`}
                                    onClick={() => handleClick(r, c)}
                                    className={`
                                        w-10 h-10 md:w-14 md:h-14
                                        flex items-center justify-center
                                        text-xl md:text-3xl cursor-pointer
                                        transition-all
                                        ${isDark ? "bg-zinc-900" : "bg-zinc-700"}
                                        ${isMove ? (isRed ? "ring-2 ring-red-500" : "ring-2 ring-blue-500") : ""}
                                    `}
                                >
                                    {piece && (
                                        <span className={`${pieceColor(piece)} drop-shadow`}>
                                            {pieceMap[piece]}
                                        </span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                <div className={`flex flex-col justify-center gap-4 min-w-[180px] p-4 border transition-all ${turnGlow}`}>
                    {!winner ? (
                        <>
                            <div className={`text-xs tracking-widest ${turnText}`}>
                                ACTIVE FACTION
                            </div>
                            <div className={`text-lg font-bold ${turnText}`}>
                                {turn.toUpperCase()}
                            </div>

                            <button
                                onClick={rollDice}
                                className={`px-4 py-2 border tracking-widest uppercase hover:bg-opacity-10 transition
                                    ${isRed
                                        ? "border-red-500 text-red-400 hover:bg-red-500"
                                        : "border-blue-500 text-blue-400 hover:bg-blue-500"
                                    }`}
                            >
                                🎲 ENGAGE
                            </button>

                            {dice && (
                                <div className={`text-xs tracking-widest ${turnText}`}>
                                    UNIT AUTHORIZED: {diceLabel[dice]}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="text-green-400 font-bold tracking-widest">
                                🏆 VICTORY ACHIEVED
                            </div>
                            <button
                                onClick={resetGame}
                                className="px-4 py-2 border border-green-500 text-green-400 hover:bg-green-500/10 tracking-widest"
                            >
                                RESET PROTOCOL
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChessPage;
